// P0 · ¿Olvidaste tu contraseña? (decisión D51). Con correo en el servidor, manda el enlace para crear una nueva (vence
// en 1 hora); sin correo, se le pide al administrador, que lo manda por WhatsApp desde el panel. En la demostración el
// enlace aparece en pantalla, como el código del celular.
import { Boton, Campo, Esqueleto, EsqueletoRenglon, EsqueletoTexto, Icono, ZonaCargando } from '@reparto/ui';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AvisoDemostracion, AvisoError, type EstadoAcceso, MarcoAcceso } from '../componentes/MarcoAcceso';
import { ErrorApi, SinRed } from '../datos/api';
import { correoValido, pedirEnlaceClave, type RespuestaOlvide, rutaDelEnlace } from '../datos/clave';
import { type OpcionesAcceso, opcionesAcceso, opcionesRecordadas } from '../datos/google';
import { ultimoCorreo } from '../datos/sesion';

const PEDIR = 'Pedile al administrador que te mande un enlace para crear una contraseña nueva.';

function Nota({ icono, children }: { icono: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3 text-[15px] dark:border-stone-800 dark:bg-stone-900">
      <Icono nombre={icono} className="mt-0.5 shrink-0 text-primary-700 dark:text-primary-300" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function Olvide() {
  const navegar = useNavigate();
  const estado = useLocation().state as EstadoAcceso | null;
  const [email, setEmail] = useState(() => estado?.email || ultimoCorreo());
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** undefined: preguntando; null: sin señal (se muestra el formulario igual). */
  const [opciones, setOpciones] = useState<OpcionesAcceso | null | undefined>(undefined);
  const [listo, setListo] = useState<RespuestaOlvide | null>(null);

  useEffect(() => {
    let vivo = true;
    void opcionesAcceso().then((o) => vivo && setOpciones(o));
    return () => {
      vivo = false;
    };
  }, []);

  const volver = () => navegar('/acceso', { state: { email: email.trim() } satisfies EstadoAcceso });
  const pedir = opciones?.recuperar === 'pedir' || listo?.modo === 'pedir';
  // Mientras pregunta, lo que hubo la última vez en este celular: el aviso o el formulario, en gris.
  const [recordadas] = useState(opcionesRecordadas);
  const previstas = opciones === undefined ? recordadas : opciones;
  // Sin correo en el servidor y fuera de la demostración no hay nada que mandar: solo se explica a quién pedírselo.
  const conFormulario = !listo && !(previstas?.recuperar === 'pedir' && !previstas.enlace_en_pantalla);

  async function mandar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const e = !email.trim()
      ? 'Escribí tu correo'
      : !correoValido(email)
        ? 'Revisá el correo: por ejemplo nombre@empresa.com'
        : null;
    setErrorCorreo(e);
    if (e) return;
    setOcupado(true);
    try {
      setListo(await pedirEnlaceClave(email));
    } catch (err) {
      if (err instanceof SinRed) setError('No hay conexión. Para pedir el enlace necesitás señal.');
      else if (err instanceof ErrorApi) setError(err.message);
      else setError('No se pudo pedir el enlace. Probá de nuevo.');
    } finally {
      setOcupado(false);
    }
  }

  function abrirEnlace(url: string) {
    const ruta = rutaDelEnlace(url);
    if (ruta) navegar(ruta);
    else window.location.assign(url);
  }

  return (
    <MarcoAcceso compacto>
      <div className="space-y-4">
        <h2 className="text-xl font-medium">¿Olvidaste tu contraseña?</h2>
        {opciones === undefined ? (
          <ZonaCargando texto="Un momento…" className="flex flex-col gap-4">
            {conFormulario ? (
              <>
                <EsqueletoTexto renglones={2} tamano="base" />
                <div>
                  <EsqueletoRenglon tamano="xs" ancho="3.5rem" className="mb-1.5" />
                  <Esqueleto className="h-12 w-full rounded-2xl" />
                </div>
                <Esqueleto className="h-14 w-full rounded-full" />
              </>
            ) : (
              <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3 dark:border-stone-800 dark:bg-stone-900">
                <Esqueleto className="mt-0.5 h-6 w-6 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-1">
                  <EsqueletoTexto renglones={2} tamano="base" />
                  <EsqueletoTexto renglones={3} />
                </div>
              </div>
            )}
          </ZonaCargando>
        ) : (
          <>
            {pedir ? (
              <Nota icono="support_agent">
                <p className="font-medium">{PEDIR}</p>
                <p className="text-sm text-stone-600 dark:text-stone-300">
                  Te llega por WhatsApp. Abrilo en este celular, elegí la contraseña y la app entra sola.
                </p>
              </Nota>
            ) : listo ? (
              <Nota icono="mark_email_read">
                <p className="font-medium">Revisá tu correo.</p>
                <p className="text-sm text-stone-600 dark:text-stone-300">
                  Si {email.trim()} tiene usuario, te llega un enlace para crear la contraseña nueva. Vence en 1 hora.
                  Mirá también en «Correo no deseado».
                </p>
              </Nota>
            ) : (
              <p className="text-[15px] text-stone-600 dark:text-stone-300">
                Escribí el correo con que entrás y te mandamos un enlace para crear una contraseña nueva.
              </p>
            )}
            {listo?.enlace_demostracion ? (
              <AvisoDemostracion>
                <p>
                  Demostración con datos simulados: este es el enlace que llegaría. Con datos reales llega por correo o
                  te lo manda el administrador.
                </p>
                <Boton
                  bloque
                  variante="secundario"
                  icono="link"
                  className="mt-3"
                  onClick={() => abrirEnlace(listo.enlace_demostracion!)}
                >
                  Abrir el enlace
                </Boton>
              </AvisoDemostracion>
            ) : null}
            {conFormulario ? (
              <form onSubmit={mandar} noValidate className="space-y-4">
                {opciones?.recuperar === 'pedir' ? (
                  <AvisoDemostracion>
                    Demostración con datos simulados: escribí tu correo y el enlace aparece acá.
                  </AvisoDemostracion>
                ) : null}
                <Campo
                  etiqueta="Correo"
                  icono="mail"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="nombre@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errorCorreo}
                />
                {error ? <AvisoError>{error}</AvisoError> : null}
                <Boton type="submit" bloque tamano="lg" icono="send" cargando={ocupado}>
                  {opciones?.recuperar === 'pedir' ? 'Ver el enlace' : 'Mandar el enlace'}
                </Boton>
              </form>
            ) : null}
          </>
        )}
        <Boton
          variante={conFormulario ? 'fantasma' : listo?.enlace_demostracion ? 'secundario' : 'primario'}
          tamano="lg"
          bloque
          icono="arrow_back"
          onClick={volver}
        >
          Volver a entrar
        </Boton>
      </div>
    </MarcoAcceso>
  );
}
